import { useMemo } from 'react';
import { UseFormReset } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { NotificationPosition } from 'core/enums';
import { useTableActions } from 'core/hooks';
import { FormProvider } from 'core/states';
import { ICustomFilter, ICustomFilterForm } from 'core/types';

import { UpsertFilterFormFields } from './UpsertFilterFormFields';

interface IProps {
  entity: string;
  currentConfig: string;
  isOpen: boolean;
  onClose: () => void;
  onSubmitSuccess?: (filter: ICustomFilter) => void;
  filter?: ICustomFilter;
}

export const UpsertFilter = ({
  entity,
  currentConfig,
  isOpen,
  onClose,
  onSubmitSuccess,
  filter,
}: IProps) => {
  const { t } = useTranslation();

  const { upsertCustomFilter } = useTableActions({
    notificationPosition: NotificationPosition.FORM,
  });

  const defaultValues = useMemo(() => {
    return {
      name: filter?.name || '',
      default: filter?.default || false,
    };
  }, [filter]);

  const schema = useMemo(() => {
    return yup.object({
      name: yup.string().label(t('labelName')).required(),
      default: yup.boolean().label(t('labelDefault')),
    });
  }, [t]);

  const onSubmit =
    (reset: UseFormReset<ICustomFilterForm>) =>
    async ({ default: isDefault, ...rest }: ICustomFilterForm) => {
      const { success, customTableFilter } = await upsertCustomFilter({
        id: filter?.id,
        entity,
        config: currentConfig,
        default: isDefault ? 1 : 0,
        ...rest,
      });

      if (!success) {
        return false;
      }

      onSubmitSuccess?.(customTableFilter);

      setTimeout(() => {
        handleOnClose(reset);
      }, 1000);
    };

  const handleOnClose = (reset: UseFormReset<ICustomFilterForm>) => {
    onClose();

    reset();
  };

  return (
    <FormProvider schema={schema} defaultValues={defaultValues} resetDefaultValues>
      <UpsertFilterFormFields
        isOpen={isOpen}
        onClose={handleOnClose}
        onSubmit={onSubmit}
        filter={filter}
      />
    </FormProvider>
  );
};
